<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Build Insights</p>
          <h2>Best builds by specialization</h2>
          <p>Gear, talents, stat signals and player samples aggregated from top Mythic+ leaderboard characters.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar gear-insights-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Season: <span>{{ analysis?.seasonLabel || 'Current' }}</span></span>
        <span class="filter-pill">Region: <span>{{ analysis?.region || filters.region }}</span></span>
        <span class="filter-pill">Players: <span>{{ analyzedCharactersCount }}</span></span>
        <span class="filter-pill">Cache: <span>{{ cacheStatusLabel }}</span></span>
      </div>
      <div class="page-actions">
        <button class="btn-icon-outline" type="button" aria-label="Refresh analysis" title="Refresh analysis" :disabled="loading" @click="refreshAnalysis">
          <i class="fa-solid fa-rotate" :class="{ 'is-spinning': loading }"></i>
        </button>
      </div>
    </div>

    <div v-if="error" class="card">
      <section>{{ error }}</section>
    </div>

    <section v-if="showSampleProgress" class="gear-insights-progress card">
      <header class="section-head">
        <div>
          <p class="eyebrow">{{ sampleProgressEyebrow }}</p>
          <h3>{{ sampleProgressTitle }}</h3>
          <small>{{ sampleProgressSummary }}</small>
        </div>
        <div v-if="progressLabel" class="gear-profile-requests" title="Completed profile requests in the current discovered set">
          <span>Profile requests</span>
          <strong>{{ progressLabel }}</strong>
        </div>
      </header>
      <div v-if="loading || batchNumber" class="gear-progress-segments" aria-label="Raider.io request budget used">
        <span
          v-for="segment in progressSegments"
          :key="segment.index"
          class="gear-progress-segment"
          :class="{ 'is-active': segment.active, 'is-complete': segment.complete, 'has-cooldown': segment.cooldown }"
        >
          <small v-if="segment.cooldown" class="gear-cooldown-badge">{{ cooldownLabel }}</small>
          <i :style="{ width: segment.width }"></i>
        </span>
      </div>
      <div class="gear-batch-status">
        <span v-if="batchNumber" class="filter-pill">Request batch: <span>{{ batchNumber }}/{{ maxBatchCount }}</span></span>
        <span v-if="elapsedLabel" class="filter-pill">Elapsed: <span>{{ elapsedLabel }}</span></span>
        <span v-if="batchProgressLabel" class="filter-pill">Requests: <span>{{ batchProgressLabel }}</span></span>
        <span v-if="cooldownLabel" class="filter-pill">Next batch: <span>{{ cooldownLabel }}</span></span>
        <span v-if="visibleFailedRequestCount" class="filter-pill">Failed requests: <span>{{ visibleFailedRequestCount }}</span></span>
        <span v-if="specMismatchCount" class="filter-pill" title="Profiles skipped because the character is currently in a different specialization than the one played in the run">Other spec: <span>{{ specMismatchCount }}</span></span>
        <span class="filter-pill">Classes: <span>{{ sampleCompletion.classesDone }}/{{ sampleCompletion.classesTotal }}</span></span>
        <span class="filter-pill">Specs: <span>{{ sampleCompletion.specsDone }}/{{ sampleCompletion.specsTotal }}</span></span>
        <span v-if="sampleCompletion.minimumProfilesNeeded" class="filter-pill">Need at least: <span>{{ sampleCompletion.minimumProfilesNeeded }} profiles</span></span>
        <span v-if="estimatedRemainingBatches" class="filter-pill">Est. batches: <span>{{ estimatedRemainingBatches }}</span></span>
        <span v-if="partialReady || (analysis && !analysis.sampleComplete)" class="filter-pill">Partial data: <span>Available</span></span>
      </div>
    </section>

    <section v-if="classOptions.length" class="gear-selector-stack">
      <article class="card gear-choice-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Class</p>
            <h3>{{ selectedClassLabel }}</h3>
          </div>
        </header>
        <div class="gear-class-switch">
          <button
            v-for="classItem in classOptions"
            :key="classItem.key"
            type="button"
            :class="{ 'is-active': selectedClassKey === classItem.key }"
            :style="{ '--class-color': classItem.color }"
            @click="selectClass(classItem.key)"
          >
            <img v-if="classItem.icon" :src="classItem.icon" :alt="classItem.name" loading="lazy">
            <span>{{ classItem.name }}</span>
            <small>({{ classItem.count }})</small>
          </button>
        </div>
      </article>

      <article
        v-if="specOptions.length"
        ref="specializationPanel"
        class="card gear-choice-panel gear-specialization-panel"
      >
        <header class="section-head">
          <div>
            <p class="eyebrow">Specialization</p>
            <h3>{{ selectedSpecLabel }}</h3>
          </div>
        </header>
        <div class="gear-spec-switch view-switch">
          <button
            v-for="spec in specOptions"
            :key="spec.key"
            class="btn-sm-outline"
            type="button"
            :class="{ 'is-active': selectedSpecKey === spec.key }"
            @click="selectedSpecKey = spec.key"
          >
            {{ spec.name }} ({{ spec.count }})
          </button>
        </div>
      </article>

      <AppEmptyState
        v-else-if="selectedClassKey"
        eyebrow="Sample pending"
        icon="fa-solid fa-hourglass-half"
        :title="`${selectedClassLabel} data is still being collected`"
        message="This class has not received enough leaderboard profiles yet. You can keep using the available classes while the background sampler fills the missing data."
      />
    </section>

    <article v-if="selectedSpecData" class="gear-insights-summary">
      <section class="mode-switch-panel gear-insights-mode-switch">
        <button
          type="button"
          :class="{ 'is-active': gearInsightsMode === 'equipment' }"
          @click="gearInsightsMode = 'equipment'"
        >
          <i class="fa-solid fa-shirt"></i>
          <span>
            <strong>Equipment</strong>
            <small>Best items by gear slot</small>
          </span>
        </button>
        <button
          type="button"
          :class="{ 'is-active': gearInsightsMode === 'talents' }"
          @click="gearInsightsMode = 'talents'"
        >
          <i class="fa-solid fa-code-branch"></i>
          <span>
            <strong>Talents</strong>
            <small>Most common builds</small>
          </span>
        </button>
        <button
          type="button"
          :class="{ 'is-active': gearInsightsMode === 'stats' }"
          @click="gearInsightsMode = 'stats'"
        >
          <i class="fa-solid fa-chart-simple"></i>
          <span>
            <strong>Stat Signals</strong>
            <small>From gems, enchants and item stats</small>
          </span>
        </button>
        <button
          type="button"
          :class="{ 'is-active': gearInsightsMode === 'players' }"
          @click="gearInsightsMode = 'players'"
        >
          <i class="fa-solid fa-table-list"></i>
          <span>
            <strong>Players</strong>
            <small>Characters in the sample</small>
          </span>
        </button>
      </section>

      <section v-if="gearInsightsMode === 'equipment'" class="card gear-sample-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Best set</p>
            <h3>{{ selectedSpecData.specName }} {{ selectedSpecData.className }}</h3>
          </div>
          <span class="filter-pill">Talent profiles: <span>{{ selectedTalentSampleCount }}</span></span>
        </header>

        <section class="armory-frame gear-insights-armory">
          <GearLoadoutShowcase
            eyebrow="Recommended loadout"
            :title="selectedSpecLabel"
            :subtitle="selectedClassLabel"
            :portrait="selectedClassOption.icon"
            portrait-icon
            :accent="selectedClassOption.color"
            :metrics="gearShowcaseMetrics"
          />

          <div class="armory-column armory-left">
            <a
              v-for="slot in leftGearSlots"
              :key="slot"
              class="armory-item"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <ItemIcon :item="bestItem(slot)" :alt="bestItem(slot)?.name || slotLabel(slot)" loading="eager" />
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
            </a>
          </div>

          <div class="armory-column armory-right">
            <a
              v-for="slot in rightGearSlots"
              :key="slot"
              class="armory-item is-right"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
              <ItemIcon :item="bestItem(slot)" :alt="bestItem(slot)?.name || slotLabel(slot)" loading="eager" />
            </a>
          </div>

          <div class="armory-weapons">
            <a
              v-for="slot in weaponGearSlots"
              :key="slot"
              class="armory-item armory-weapon"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <ItemIcon :item="bestItem(slot)" :alt="bestItem(slot)?.name || slotLabel(slot)" loading="eager" />
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
            </a>
          </div>
        </section>
      </section>

      <section v-else-if="gearInsightsMode === 'talents'" class="card gear-insights-detail-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Talent builds</p>
            <h3>{{ selectedSpecLabel }} {{ selectedClassLabel }}</h3>
          </div>
          <div class="gear-character-stats applied-filters">
            <span class="filter-pill">Shown: <span>{{ visibleTalentBuilds.length }}/{{ selectedTalentBuilds.length }}</span></span>
            <span class="filter-pill">Talent profiles: <span>{{ selectedTalentSampleCount }}</span></span>
          </div>
        </header>

        <div v-if="selectedTalentBuilds.length" class="gear-talent-list">
          <article
            v-for="build in visibleTalentBuilds"
            :key="build.loadout"
            class="gear-talent-row"
          >
            <button class="gear-talent-main" type="button" @click="openTalentBuild(build)">
              <span class="gear-top-rank">#{{ build.rank }}</span>
              <span>
                <strong>{{ build.label }}</strong>
                <small>{{ build.count }} {{ build.count === 1 ? 'player' : 'players' }} &middot; {{ build.share }}% of talent profiles</small>
              </span>
              <code>{{ build.shortLoadout }}</code>
            </button>
            <button
              class="btn-icon-outline gear-talent-copy"
              type="button"
              aria-label="Copy talent loadout"
              title="Copy talent loadout"
              @click="copyTalentLoadout(build)"
            >
              <i class="fa-solid fa-copy"></i>
            </button>
          </article>
        </div>
        <div v-else class="gear-character-empty">
          No talent loadout data is available for this specialization sample.
        </div>
        <AppPagination
          v-if="talentTotalPages > 1"
          v-model:page="talentPage"
          :total-pages="talentTotalPages"
          aria-label="Talent builds pagination"
        />
      </section>

      <section v-else-if="gearInsightsMode === 'stats'" class="card gear-insights-detail-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Stat signals</p>
            <h3>{{ selectedSpecLabel }} {{ selectedClassLabel }}</h3>
          </div>
          <span class="filter-pill">Sample: <span>{{ selectedStatSampleCount }}</span></span>
        </header>

        <div v-if="selectedStatRows.length" class="gear-stat-grid">
          <article v-for="stat in selectedStatRows" :key="stat.key" class="gear-stat-card">
            <span>{{ stat.label }}</span>
            <strong>{{ stat.average || `${stat.share}%` }}</strong>
            <small>{{ stat.average ? `Average from ${stat.count} profiles` : `${stat.profileCount}/${stat.sampleCount} profiles / ${stat.sources}` }}</small>
          </article>
        </div>
        <div v-else class="gear-character-empty">
          Raider.io did not return stat values, gems, enchants or item stat data for this sample.
        </div>
      </section>

      <section v-else class="card gear-sample-characters">
        <header class="section-head">
          <div>
            <p class="eyebrow">Sample characters</p>
            <h3>{{ selectedSpecLabel }} {{ selectedClassLabel }}</h3>
          </div>
        </header>

        <div class="gear-character-tools">
          <label class="quick-search-field" for="gear-character-search">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input
              id="gear-character-search"
              v-model.trim="characterSearch"
              class="input"
              type="search"
              placeholder="Search by name, realm or region"
              autocomplete="off"
            >
          </label>
          <div class="gear-character-stats applied-filters">
            <span class="filter-pill">Shown: <span>{{ visibleSpecCharacters.length }}/{{ filteredSpecCharacters.length }}</span></span>
            <span class="filter-pill">Sample: <span>{{ selectedSpecCharacters.length }}</span></span>
          </div>
        </div>

        <div v-if="filteredSpecCharacters.length" class="gear-character-list">
          <article v-for="character in visibleSpecCharacters" :key="character.key" class="gear-character-row">
            <a class="gear-character-identity" :href="character.profileUrl" target="_blank" rel="noreferrer">
              <span class="gear-character-avatar">
                <img v-if="character.thumbnailUrl" :src="character.thumbnailUrl" :alt="character.name" loading="lazy">
                <i v-else class="fa-solid fa-user"></i>
              </span>
              <span class="gear-character-copy">
                <strong>{{ character.name }}</strong>
                <small>{{ character.regionLabel }} &middot; {{ character.realm }}</small>
              </span>
            </a>
            <span class="gear-character-origin">
              <img :src="factionIcon(character.faction)" :alt="character.factionLabel" loading="lazy">
              <span>
                <strong>{{ character.factionLabel }}</strong>
                <small>{{ character.raceLabel }}</small>
              </span>
            </span>
            <span>
              <strong>{{ character.itemLevelLabel }}</strong>
              <small>Item level</small>
            </span>
            <span>
              <strong>{{ character.scoreLabel }}</strong>
              <small>M+ score</small>
            </span>
            <button
              class="btn-sm-outline"
              type="button"
              :disabled="addingFavoriteKey === character.key"
              @click="addSampleCharacterToFavorites(character)"
            >
              <i class="fa-solid" :class="addingFavoriteKey === character.key ? 'fa-spinner is-spinning' : 'fa-star'"></i>
              <span>{{ addingFavoriteKey === character.key ? 'Adding' : 'Favorite' }}</span>
            </button>
          </article>
        </div>
        <div v-else class="gear-character-empty">
          No characters match this search.
        </div>

        <AppPagination
          v-if="characterTotalPages > 1"
          v-model:page="characterPage"
          :total-pages="characterTotalPages"
          aria-label="Sample characters pagination"
        />
      </section>
    </article>

    <AppEmptyState
      v-else-if="!loading"
      icon="fa-solid fa-shirt"
      title="No gear analysis yet"
      message="Refresh the page data to analyze leaderboard characters and build specialization gear picks."
      :actions="[]"
    />

    <div
      v-if="activeSlot"
      class="app-modal-backdrop gear-backdrop"
      :class="{ 'is-closing': closingSlot }"
      @click.self="closeSlot"
    >
      <section class="app-modal gear-top-modal" role="dialog" aria-modal="true" aria-labelledby="gear-top-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Top 5 items</p>
            <h2 id="gear-top-title">{{ slotLabel(activeSlot) }}</h2>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close item list" @click="closeSlot">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>
        <section class="gear-top-list">
          <a
            v-for="(item, index) in activeSlotItems"
            :key="`${activeSlot}-${item.itemId}`"
            class="gear-top-item"
            :href="wowheadItemUrl(item)"
            :data-wowhead="wowheadItemData(item)"
            target="_blank"
            rel="noreferrer"
          >
            <span class="gear-top-rank">#{{ index + 1 }}</span>
            <ItemIcon :item="item" :alt="item.name" loading="eager" />
            <span>
              <strong>{{ item.name }}</strong>
              <small>{{ item.count }} equipped · {{ item.share }}%</small>
            </span>
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
          </a>
        </section>
      </section>
    </div>

    <div
      v-if="activeTalentBuild"
      class="app-modal-backdrop talents-backdrop"
      :class="{ 'is-closing': closingTalentBuild }"
      @click.self="closeTalentBuild"
    >
      <section class="app-modal talents-modal" role="dialog" aria-modal="true" aria-labelledby="gear-talents-modal-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Wowhead Talent Calculator</p>
            <h2 id="gear-talents-modal-title">{{ activeTalentBuild.label }} &middot; {{ selectedSpecLabel }} {{ selectedClassLabel }}</h2>
          </div>
          <div class="modal-actions">
            <button class="btn-icon-outline" type="button" aria-label="Close talents" @click="closeTalentBuild">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </header>

        <section class="talents-frame">
          <iframe
            :src="talentEmbedUrl(activeTalentBuild.loadout)"
            title="Wowhead talent calculator"
            loading="lazy"
            scrolling="no"
            referrerpolicy="no-referrer-when-downgrade"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          ></iframe>
        </section>

        <a
          class="btn-sm-outline"
          :href="talentCalculatorUrl(activeTalentBuild.loadout)"
          target="_blank"
          rel="noreferrer"
        >
          <i class="fa-solid fa-arrow-up-right-from-square"></i> Open Wowhead
        </a>
      </section>
    </div>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import AppPagination from '../components/AppPagination.vue'
import GearLoadoutShowcase from '../components/GearLoadoutShowcase.vue'
import { getCharacterProfile, getMythicPlusRuns, getMythicPlusStaticData } from '../services/raiderApi'
import deathKnightIcon from '../assets/images/classes/death knight.png'
import demonHunterIcon from '../assets/images/classes/demon hunter.png'
import druidIcon from '../assets/images/classes/druid.png'
import evokerIcon from '../assets/images/classes/evoker.png'
import hunterIcon from '../assets/images/classes/hunter.png'
import mageIcon from '../assets/images/classes/mage.png'
import monkIcon from '../assets/images/classes/monk.png'
import paladinIcon from '../assets/images/classes/paladin.png'
import priestIcon from '../assets/images/classes/priest.png'
import rogueIcon from '../assets/images/classes/rogue.png'
import shamanIcon from '../assets/images/classes/shaman.png'
import warlockIcon from '../assets/images/classes/warlock.png'
import warriorIcon from '../assets/images/classes/warrior.png'
import allianceBanner from '../assets/images/alliance-banner.svg'
import allianceHordeBanner from '../assets/images/alliance-horde-banner.svg'
import hordeBanner from '../assets/images/horde-banner.svg'
import ItemIcon from '../components/ItemIcon.vue'
import {
  gearInsightsNotification,
  gearInsightsSampleStatus,
  hasUsableGearInsightsCache,
} from '../utils/gearInsightsStatus'
import { ensureWowheadTooltips, refreshWowheadTooltips } from '../utils/wowheadTooltips'

const CACHE_KEY = 'rio-dashboard-gear-insights'
const CACHE_VERSION = 28
const MIN_LEADERBOARD_PAGE_COUNT = 5
const MAX_LEADERBOARD_PAGE_COUNT = 40
const LEADERBOARD_PAGE_BATCH_SIZE = 1
const PROFILE_BATCH_SIZE = 5
const PROFILE_REQUEST_RETRY_LIMIT = 2
const ANALYSIS_BATCH_REQUEST_COUNT = 100
const MAX_ANALYSIS_BATCH_COUNT = 8
const ANALYSIS_BATCH_PAUSE_MS = 20 * 1000
const TOP_ITEM_LIMIT = 5
const MIN_CHARACTERS_PER_SPEC = 12
// A leaderboard source is abandoned after this many consecutive pages without new characters
// for any specialization that is still short. Deep pages of the same ladder mostly repeat the
// same meta teams, so the request budget is better spent on profiles.
const SOURCE_IDLE_PAGE_LIMIT = 3
const TALENT_BUILDS_PAGE_SIZE = 10

const STAT_KEYS = [
  { key: 'crit', label: 'Critical Strike', aliases: ['crit', 'critical_strike', 'criticalStrike', 'crit_rating', 'critRating'] },
  { key: 'haste', label: 'Haste', aliases: ['haste', 'haste_rating', 'hasteRating'] },
  { key: 'mastery', label: 'Mastery', aliases: ['mastery', 'mast', 'mastery_rating', 'masteryRating'] },
  { key: 'versatility', label: 'Versatility', aliases: ['versatility', 'versatility_rating', 'versatilityRating', 'vers'] },
]

const ALL_CLASSES = [
  { key: 'death-knight', name: 'Death Knight' },
  { key: 'demon-hunter', name: 'Demon Hunter' },
  { key: 'druid', name: 'Druid' },
  { key: 'evoker', name: 'Evoker' },
  { key: 'hunter', name: 'Hunter' },
  { key: 'mage', name: 'Mage' },
  { key: 'monk', name: 'Monk' },
  { key: 'paladin', name: 'Paladin' },
  { key: 'priest', name: 'Priest' },
  { key: 'rogue', name: 'Rogue' },
  { key: 'shaman', name: 'Shaman' },
  { key: 'warlock', name: 'Warlock' },
  { key: 'warrior', name: 'Warrior' },
]

const CLASS_SPECIALIZATIONS = {
  'death-knight': ['blood', 'frost', 'unholy'],
  'demon-hunter': ['devourer', 'havoc', 'vengeance'],
  druid: ['balance', 'feral', 'guardian', 'restoration'],
  evoker: ['augmentation', 'devastation', 'preservation'],
  hunter: ['beast-mastery', 'marksmanship', 'survival'],
  mage: ['arcane', 'fire', 'frost'],
  monk: ['brewmaster', 'mistweaver', 'windwalker'],
  paladin: ['holy', 'protection', 'retribution'],
  priest: ['discipline', 'holy', 'shadow'],
  rogue: ['assassination', 'outlaw', 'subtlety'],
  shaman: ['elemental', 'enhancement', 'restoration'],
  warlock: ['affliction', 'demonology', 'destruction'],
  warrior: ['arms', 'fury', 'protection'],
}

const CLASS_ICONS = {
  deathknight: deathKnightIcon,
  'death-knight': deathKnightIcon,
  'death knight': deathKnightIcon,
  demonhunter: demonHunterIcon,
  'demon-hunter': demonHunterIcon,
  'demon hunter': demonHunterIcon,
  druid: druidIcon,
  evoker: evokerIcon,
  hunter: hunterIcon,
  mage: mageIcon,
  monk: monkIcon,
  paladin: paladinIcon,
  priest: priestIcon,
  rogue: rogueIcon,
  shaman: shamanIcon,
  warlock: warlockIcon,
  warrior: warriorIcon,
}

const CLASS_COLORS = {
  deathknight: '#c41e3a',
  'death-knight': '#c41e3a',
  'death knight': '#c41e3a',
  demonhunter: '#a330c9',
  'demon-hunter': '#a330c9',
  'demon hunter': '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const FACTION_ICONS = {
  alliance: allianceBanner,
  horde: hordeBanner,
  mixed: allianceHordeBanner,
}

const SLOT_LABELS = {
  head: 'Head',
  neck: 'Neck',
  shoulder: 'Shoulder',
  back: 'Back',
  chest: 'Chest',
  wrist: 'Wrist',
  hands: 'Hands',
  waist: 'Waist',
  legs: 'Legs',
  feet: 'Feet',
  finger1: 'Finger',
  finger2: 'Finger',
  finger: 'Finger',
  trinket1: 'Trinket',
  trinket2: 'Trinket',
  trinket: 'Trinket',
  mainhand: 'Main hand',
  offhand: 'Off hand',
}

function normalizeKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
}

function normalizeClassKey(value) {
  const key = normalizeKey(value)
  const aliases = {
    deathknight: 'death-knight',
    demonhunter: 'demon-hunter',
  }
  return aliases[key] || key
}

function normalizeSlotKey(slot) {
  return String(slot || '')
    .toLowerCase()
    .replace(/_/g, '')
    .replace(/-/g, '')
    .replace(/\s/g, '')
}

function loadCachedAnalysis() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    return cached?.cacheVersion === CACHE_VERSION ? cached : null
  } catch (error) {
    return null
  }
}

function hasUsableCachedAnalysis(payload) {
  return hasUsableGearInsightsCache(payload)
}

function saveCachedAnalysis(payload) {
  const value = JSON.stringify(payload)

  try {
    localStorage.setItem(CACHE_KEY, value)
  } catch (error) {
    // The analysis payload is large; drop the previous entry and retry once so a full
    // quota never discards a result that is already computed.
    try {
      clearCachedAnalysis()
      localStorage.setItem(CACHE_KEY, value)
    } catch (retryError) {
      console.warn('Unable to cache the build analysis', retryError)
    }
  }
}

function clearCachedAnalysis() {
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch (error) {
    console.warn('Unable to clear the cached build analysis', error)
  }
}

const analysisJobState = {
  loading: false,
  loadingMessage: 'Preparing leaderboard data',
  progressCurrent: 0,
  progressTotal: 0,
  batchNumber: 0,
  batchRequestCount: 0,
  batchRequestLimit: ANALYSIS_BATCH_REQUEST_COUNT,
  failedRequestCount: 0,
  specMismatchCount: 0,
  cooldownRemaining: 0,
  partialReady: false,
  sampleCompletion: null,
  startedAt: 0,
  elapsedSeconds: 0,
  error: '',
  result: null,
  promise: null,
}

const analysisJobListeners = new Set()

function analysisJobSnapshot() {
  return { ...analysisJobState }
}

function notifyAnalysisJob() {
  const snapshot = analysisJobSnapshot()
  analysisJobListeners.forEach((listener) => listener(snapshot))
}

function setAnalysisJobState(patch) {
  Object.assign(analysisJobState, patch)
  if (analysisJobState.loading && analysisJobState.startedAt) {
    analysisJobState.elapsedSeconds = Math.max(0, Math.floor((Date.now() - analysisJobState.startedAt) / 1000))
  }
  notifyAnalysisJob()
}

function subscribeAnalysisJob(listener) {
  analysisJobListeners.add(listener)
  listener(analysisJobSnapshot())
  return () => analysisJobListeners.delete(listener)
}

async function waitForAnalysisCooldown() {
  const seconds = Math.ceil(ANALYSIS_BATCH_PAUSE_MS / 1000)
  for (let remaining = seconds; remaining > 0; remaining -= 1) {
    setAnalysisJobState({
      loadingMessage: 'Waiting before the next Raider.io batch',
      cooldownRemaining: remaining,
    })
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
  setAnalysisJobState({
    cooldownRemaining: 0,
  })
}

export default {
  name: 'GearInsightsView',
  components: { AppEmptyState, AppPagination, GearLoadoutShowcase, ItemIcon },
  data() {
    return {
      loading: false,
      loadingMessage: 'Preparing leaderboard data',
      progressCurrent: 0,
      progressTotal: 0,
      batchNumber: 0,
      batchRequestCount: 0,
      batchRequestLimit: ANALYSIS_BATCH_REQUEST_COUNT,
      failedRequestCount: 0,
      specMismatchCount: 0,
      cooldownRemaining: 0,
      partialReady: false,
      liveSampleCompletion: null,
      elapsedSeconds: 0,
      error: '',
      analysis: null,
      selectedClassKey: '',
      selectedSpecKey: '',
      activeSlot: null,
      closingSlot: false,
      activeTalentBuild: null,
      closingTalentBuild: false,
      gearInsightsMode: 'equipment',
      talentPage: 1,
      characterSearch: '',
      characterPage: 1,
      characterPageSize: 25,
      addingFavoriteKey: '',
      unsubscribeAnalysisJob: null,
      filters: {
        expansionId: 11,
        region: 'world',
        dungeon: 'all',
        season: '',
      },
      leftGearSlots: ['head', 'neck', 'shoulder', 'back', 'chest', 'shirt', 'tabard', 'wrist'],
      rightGearSlots: ['hands', 'waist', 'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2'],
      weaponGearSlots: ['mainhand', 'offhand'],
    }
  },
  computed: {
    cacheStatusLabel() {
      if (!this.analysis?.createdAt) return 'Empty'
      const age = Date.now() - Number(this.analysis.createdAt)
      const coverage = this.analysis.sampleComplete ? 'Complete' : 'Partial'
      if (age < 60 * 1000) return `${coverage} · just now`
      if (age < 60 * 60 * 1000) return `${coverage} · ${Math.round(age / 60000)} min ago`
      return `${coverage} · ${Math.round(age / 3600000)} h ago`
    },
    analyzedCharactersCount() {
      return this.analysis?.characterCount || 0
    },
    visibleFailedRequestCount() {
      if (this.loading) return this.failedRequestCount
      return Number(this.analysis?.failedProfileRequests || 0) + Number(this.analysis?.failedLeaderboardRequests || 0)
    },
    classOptions() {
      const classes = this.analysis?.classes || {}
      const knownClassOptions = ALL_CLASSES.map((classItem) => {
        const item = classes[classItem.key] || {}
        return {
          key: classItem.key,
          name: item.name || classItem.name,
          color: CLASS_COLORS[classItem.key] || 'var(--primary)',
          icon: CLASS_ICONS[classItem.key],
          count: item.characterCount || 0,
        }
      })
      const customClassOptions = Object.entries(classes)
        .filter(([key]) => !ALL_CLASSES.some((classItem) => classItem.key === key))
        .map(([key, item]) => ({
          key,
          name: item.name || key,
          color: CLASS_COLORS[key] || 'var(--primary)',
          icon: CLASS_ICONS[key],
          count: item.characterCount || 0,
        }))
        .sort((left, right) => left.name.localeCompare(right.name))

      return [...knownClassOptions, ...customClassOptions]
    },
    specOptions() {
      const specs = this.analysis?.classes?.[this.selectedClassKey]?.specs || {}
      return Object.entries(specs)
        .map(([key, item]) => ({
          key,
          name: item.name || item.specName || 'Unknown',
          count: item.characterCount || 0,
        }))
        .sort((left, right) => left.name.localeCompare(right.name))
    },
    selectedClassLabel() {
      return this.classOptions.find((item) => item.key === this.selectedClassKey)?.name || 'Choose class'
    },
    selectedClassOption() {
      return this.classOptions.find((item) => item.key === this.selectedClassKey) || {
        color: 'var(--primary)',
        icon: '',
      }
    },
    selectedSpecLabel() {
      return this.specOptions.find((item) => item.key === this.selectedSpecKey)?.name || 'Choose spec'
    },
    selectedSpecData() {
      return this.analysis?.classes?.[this.selectedClassKey]?.specs?.[this.selectedSpecKey] || null
    },
    selectedTalentBuilds() {
      return this.selectedSpecData?.talentBuilds || []
    },
    talentTotalPages() {
      return Math.max(1, Math.ceil(this.selectedTalentBuilds.length / TALENT_BUILDS_PAGE_SIZE))
    },
    visibleTalentBuilds() {
      const page = Math.min(this.talentPage, this.talentTotalPages)
      const start = (page - 1) * TALENT_BUILDS_PAGE_SIZE
      return this.selectedTalentBuilds.slice(start, start + TALENT_BUILDS_PAGE_SIZE)
    },
    selectedTalentSampleCount() {
      return this.selectedSpecData?.talentSampleCount || 0
    },
    selectedStatRows() {
      return this.selectedSpecData?.stats?.length ? this.selectedSpecData.stats : (this.selectedSpecData?.statSignals || [])
    },
    selectedStatSampleCount() {
      return this.selectedSpecData?.stats?.length
        ? this.selectedSpecData.statSampleCount || 0
        : this.selectedSpecData?.statSignalSampleCount || 0
    },
    selectedSpecCharacters() {
      return this.selectedSpecData?.characters || []
    },
    filteredSpecCharacters() {
      const search = this.characterSearch.toLowerCase()
      if (!search) return this.selectedSpecCharacters

      return this.selectedSpecCharacters.filter((character) => {
        return [
          character.name,
          character.realm,
          character.region,
          character.regionLabel,
        ].some((value) => String(value || '').toLowerCase().includes(search))
      })
    },
    characterTotalPages() {
      return Math.max(1, Math.ceil(this.filteredSpecCharacters.length / this.characterPageSize))
    },
    visibleSpecCharacters() {
      const page = Math.min(this.characterPage, this.characterTotalPages)
      const start = (page - 1) * this.characterPageSize
      return this.filteredSpecCharacters.slice(start, start + this.characterPageSize)
    },
    displaySlotOrder() {
      return [...this.leftGearSlots, ...this.rightGearSlots, ...this.weaponGearSlots]
    },
    selectedBestItems() {
      if (!this.selectedSpecData?.slots) return {}

      return this.displaySlotOrder.reduce((items, slot) => {
        items[slot] = this.itemForDisplaySlot(slot)
        return items
      }, {})
    },
    gearShowcaseMetrics() {
      const selectedItems = Object.values(this.selectedBestItems).filter(Boolean).length
      return [
        { label: 'Profiles', value: this.selectedSpecData?.characterCount || this.selectedTalentSampleCount || 0 },
        { label: 'Coverage', value: `${selectedItems}/${this.displaySlotOrder.length}` },
      ]
    },
    activeSlotItems() {
      return this.itemsForSlot(this.activeSlot).slice(0, TOP_ITEM_LIMIT)
    },
    showSampleProgress() {
      return this.loading
    },
    sampleProgressEyebrow() {
      if (this.loading) return this.partialReady ? 'Background sampling' : 'Analyzing'
      return 'Partial sample'
    },
    sampleProgressTitle() {
      if (this.loading) return this.loadingMessage
      if (this.analysis?.requestLimitReached) return 'Raider.io rate limit stopped the scan'
      if (this.analysis?.batchLimitReached) return 'The scan reached its request cap'
      return 'Sample goals are not fully covered yet'
    },
    sampleProgressSummary() {
      const parts = []
      if (this.sampleCompletion.missingClasses) {
        parts.push(`${this.sampleCompletion.missingClasses} classes incomplete`)
      }
      if (this.sampleCompletion.missingSpecs) {
        parts.push(`${this.sampleCompletion.missingSpecs} specs below ${MIN_CHARACTERS_PER_SPEC} profiles`)
      }
      if (!parts.length) return 'Class and specialization coverage goals are complete.'
      const requestBudgetNote = this.loading ? ' The segmented bar shows request budget usage, not completion percentage.' : ''
      return `${parts.join(', ')}. Current data is available but remains partial.${requestBudgetNote}`
    },
    sampleCompletion() {
      if (this.liveSampleCompletion) return this.liveSampleCompletion

      const classes = this.analysis?.classes || {}
      const specCounts = ALL_CLASSES.reduce((counts, classItem) => {
        counts[classItem.key] = {}
        ;(CLASS_SPECIALIZATIONS[classItem.key] || []).forEach((specKey) => {
          counts[classItem.key][specKey] = classes[classItem.key]?.specs?.[specKey]?.characterCount || 0
        })
        return counts
      }, {})
      return this.sampleCompletionFromCounts(specCounts)
    },
    estimatedRemainingBatches() {
      if (!this.sampleCompletion.minimumProfilesNeeded) return ''
      const remainingByProfiles = Math.max(1, Math.ceil(this.sampleCompletion.minimumProfilesNeeded / ANALYSIS_BATCH_REQUEST_COUNT))
      return Math.min(Math.max(0, MAX_ANALYSIS_BATCH_COUNT - this.batchNumber + 1), remainingByProfiles)
    },
    maxBatchCount() {
      return MAX_ANALYSIS_BATCH_COUNT
    },
    elapsedLabel() {
      if (!this.elapsedSeconds) return ''
      const minutes = Math.floor(this.elapsedSeconds / 60)
      const seconds = this.elapsedSeconds % 60
      return `${minutes}:${String(seconds).padStart(2, '0')}`
    },
    progressLabel() {
      if (!this.progressTotal) return ''
      return `${this.progressCurrent}/${this.progressTotal}`
    },
    progressPercent() {
      if (!this.progressTotal && this.loading) {
        return `${Math.max(8, Math.min(100, Math.round((this.batchRequestCount / this.batchRequestLimit) * 100)))}%`
      }
      if (!this.progressTotal) return '8%'
      return `${Math.max(8, Math.round((this.progressCurrent / this.progressTotal) * 100))}%`
    },
    progressSegments() {
      return Array.from({ length: MAX_ANALYSIS_BATCH_COUNT }, (_, index) => {
        const segmentNumber = index + 1
        const complete = this.batchNumber > segmentNumber || (this.batchNumber === segmentNumber && this.batchRequestCount >= this.batchRequestLimit)
        const active = this.batchNumber === segmentNumber && !complete
        const cooldown = Boolean(this.cooldownRemaining && this.batchNumber + 1 === segmentNumber)
        const fill = complete
          ? 100
          : active
            ? Math.max(4, Math.min(100, Math.round((this.batchRequestCount / this.batchRequestLimit) * 100)))
            : 0

        return {
          index: segmentNumber,
          complete,
          active,
          cooldown,
          width: `${fill}%`,
        }
      })
    },
    batchProgressLabel() {
      if (!this.loading || !this.batchNumber) return ''
      return `${this.batchRequestCount}/${this.batchRequestLimit}`
    },
    cooldownLabel() {
      if (!this.cooldownRemaining) return ''
      return `${this.cooldownRemaining}s pause`
    },
  },
  mounted() {
    this.unsubscribeAnalysisJob = subscribeAnalysisJob(this.syncAnalysisJob)
    this.ensureWowheadTooltips()
    this.loadAnalysis()
    window.addEventListener('keydown', this.closeSlotOnEscape)
  },
  watch: {
    selectedClassKey() {
      this.resetCharacterList()
      this.resetTalentList()
    },
    selectedSpecKey() {
      this.resetCharacterList()
      this.resetTalentList()
    },
    characterSearch() {
      this.characterPage = 1
    },
    gearInsightsMode(mode) {
      if (mode === 'equipment') {
        this.$nextTick(() => this.refreshWowheadTooltips())
      }
    },
    characterTotalPages(totalPages) {
      if (this.characterPage > totalPages) {
        this.characterPage = totalPages
      }
    },
    talentTotalPages(totalPages) {
      if (this.talentPage > totalPages) {
        this.talentPage = totalPages
      }
    },
  },
  beforeUnmount() {
    if (this.unsubscribeAnalysisJob) {
      this.unsubscribeAnalysisJob()
      this.unsubscribeAnalysisJob = null
    }
    window.removeEventListener('keydown', this.closeSlotOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  methods: {
    syncAnalysisJob(snapshot) {
      this.loading = snapshot.loading
      this.loadingMessage = snapshot.loadingMessage
      this.progressCurrent = snapshot.progressCurrent
      this.progressTotal = snapshot.progressTotal
      this.batchNumber = snapshot.batchNumber
      this.batchRequestCount = snapshot.batchRequestCount
      this.batchRequestLimit = snapshot.batchRequestLimit
      this.failedRequestCount = snapshot.failedRequestCount
      this.specMismatchCount = snapshot.specMismatchCount
      this.cooldownRemaining = snapshot.cooldownRemaining
      this.partialReady = snapshot.partialReady
      this.liveSampleCompletion = snapshot.sampleCompletion
      this.elapsedSeconds = snapshot.elapsedSeconds
      this.error = snapshot.error

      if (snapshot.result && snapshot.result.createdAt !== this.analysis?.createdAt) {
        this.applyAnalysis(snapshot.result)
      }
    },
    async loadAnalysis() {
      if (analysisJobState.loading) {
        return
      }

      const cached = loadCachedAnalysis()
      if (cached?.analysis) {
        this.applyAnalysis(cached)
      }
      if (hasUsableCachedAnalysis(cached)) {
        return
      }

      await this.buildAnalysis()
    },
    async refreshAnalysis() {
      clearCachedAnalysis()
      if (!analysisJobState.loading) {
        analysisJobState.promise = null
      }
      setAnalysisJobState({
        result: null,
        error: '',
      })
      this.analysis = null
      await this.buildAnalysis(true)
    },
    applyAnalysis(payload) {
      const previousClassKey = this.selectedClassKey
      const previousSpecKey = this.selectedSpecKey
      this.analysis = payload
      const hasPreviousClass = this.classOptions.some((classItem) => classItem.key === previousClassKey)
      this.selectedClassKey = hasPreviousClass ? previousClassKey : this.classOptions[0]?.key || ''
      const hasPreviousSpec = this.specOptions.some((spec) => spec.key === previousSpecKey)
      this.selectedSpecKey = hasPreviousSpec ? previousSpecKey : this.specOptions[0]?.key || ''
      if (!hasPreviousClass || !hasPreviousSpec) {
        this.resetCharacterList()
        this.resetTalentList()
      }
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    selectClass(classKey) {
      this.selectedClassKey = classKey
      this.selectedSpecKey = this.specOptions[0]?.key || ''
      if (!this.specOptions.length) return

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const panel = this.$refs.specializationPanel
          if (!panel) return

          panel.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          })
        })
      })
    },
    async buildAnalysis(force = false) {
      if (analysisJobState.promise) {
        return analysisJobState.promise
      }

      setAnalysisJobState({
        loading: true,
        loadingMessage: 'Loading Mythic+ leaderboard',
        progressCurrent: 0,
        progressTotal: 0,
        batchNumber: 1,
        batchRequestCount: 0,
        batchRequestLimit: ANALYSIS_BATCH_REQUEST_COUNT,
        failedRequestCount: 0,
        specMismatchCount: 0,
        cooldownRemaining: 0,
        partialReady: false,
        sampleCompletion: null,
        startedAt: Date.now(),
        elapsedSeconds: 0,
        error: '',
        result: null,
      })
      const loadingId = await this.$store.dispatch('startGlobalLoading', { id: 'page:gear-insights', message: 'Analyzing leaderboard gear...' })

      const promise = (async () => {
        const staticData = await getMythicPlusStaticData(this.filters.expansionId, { cache: false })
        const season = this.pickDefaultSeason(staticData.seasons || [])
        const seasonLabel = season?.short_name || season?.name || 'Current'
        const runFilters = {
          season: season?.slug || '',
          region: this.filters.region,
          dungeon: this.filters.dungeon,
        }
        const createPayload = (characters, profiles, rosterLoadouts, options = {}) => {
          const sampleComplete = this.hasCompleteSample(profiles)
          const sampleCompletion = this.sampleCompletionFromCounts(this.profileSpecCounts(profiles))
          const requestLimitReached = Boolean(options.requestLimitReached)
          const batchLimitReached = Boolean(options.batchLimitReached)

          return {
            cacheVersion: CACHE_VERSION,
            createdAt: Date.now(),
            season: season?.slug || '',
            seasonLabel,
            region: this.filters.region,
            dungeon: this.filters.dungeon,
            characterCount: profiles.length,
            talentProfileCount: rosterLoadouts.length,
            leaderboardCharacterCount: characters.length,
            analysis: true,
            sampleComplete,
            sampleCompletion,
            sampleStatus: gearInsightsSampleStatus({ sampleComplete, requestLimitReached, batchLimitReached }),
            requestLimitReached,
            batchLimitReached,
            failedProfileRequests: Number(options.failedProfileRequests || 0),
            failedLeaderboardRequests: Number(options.failedLeaderboardRequests || 0),
            specMismatchProfiles: Number(options.specMismatchProfiles || 0),
            classes: this.aggregateProfiles(profiles, rosterLoadouts),
          }
        }

        const publishPartialAnalysis = ({ characters, profiles, rosterLoadouts, ...options }) => {
          if (!profiles.length && !rosterLoadouts.length) return
          const payload = createPayload(characters, profiles, rosterLoadouts, options)
          saveCachedAnalysis(payload)
          setAnalysisJobState({
            result: payload,
            partialReady: true,
          })
        }

        const {
          characters,
          profiles,
          rosterLoadouts,
          requestLimitReached,
          batchLimitReached,
          failedProfileRequests,
          failedLeaderboardRequests,
          specMismatchProfiles,
        } = await this.loadProfilesForSpecMinimums(
          runFilters,
          this.leaderboardSources(staticData, season),
          publishPartialAnalysis
        )

        setAnalysisJobState({
          loadingMessage: 'Aggregating best items',
        })
        const payload = createPayload(characters, profiles, rosterLoadouts, {
          requestLimitReached,
          batchLimitReached,
          failedProfileRequests,
          failedLeaderboardRequests,
          specMismatchProfiles,
        })

        saveCachedAnalysis(payload)
        setAnalysisJobState({
          result: payload,
          partialReady: !payload.sampleComplete,
        })
        this.$store.dispatch('setSeasonLabel', seasonLabel)
        this.$store.dispatch('notify', gearInsightsNotification(payload, { force }))
        return payload
      })()

      analysisJobState.promise = promise

      try {
        return await promise
      } catch (error) {
        const message = 'Unable to build insights. Try refreshing again later.'
        setAnalysisJobState({
          error: message,
        })
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Build insights failed',
          description: message,
        })
      } finally {
        // Reset the transient counters so a later mount or rerun never shows stale batch,
        // cooldown or elapsed values from a finished scan.
        setAnalysisJobState({
          loading: false,
          loadingMessage: 'Preparing leaderboard data',
          progressCurrent: 0,
          progressTotal: 0,
          batchNumber: 0,
          batchRequestCount: 0,
          specMismatchCount: 0,
          cooldownRemaining: 0,
          partialReady: false,
          sampleCompletion: null,
          startedAt: 0,
          elapsedSeconds: 0,
          promise: null,
        })
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    pickDefaultSeason(seasons) {
      const seasonOptions = seasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
      const now = Date.now()
      return seasonOptions.find((season) => {
        const start = new Date(season.starts?.us || season.starts?.eu || 0).getTime()
        const end = new Date(season.ends?.us || season.ends?.eu || 0).getTime()
        return start <= now && now <= end
      }) || seasonOptions[0] || null
    },
    uniqueCharacters(rankings) {
      const map = new Map()
      rankings.forEach((entry) => {
        ;(entry.run?.roster || []).forEach((member) => {
          const character = member.character || {}
          const key = this.characterIdentityKey(character)
          if (!character.name) return
          const specKey = this.rosterSpecKey(character)
          const existing = map.get(key)
          if (existing) {
            // The same character can appear in several runs with different specializations.
            if (specKey && !existing.rosterSpecKeys.includes(specKey)) existing.rosterSpecKeys.push(specKey)
            return
          }
          map.set(key, {
            ...character,
            fallbackClassName: character.class?.name || character.class?.slug || '',
            fallbackSpecName: character.spec?.name || character.spec?.slug || '',
            rosterSpecKeys: specKey ? [specKey] : [],
          })
        })
      })
      return Array.from(map.values())
    },
    rosterClassKey(character) {
      return normalizeClassKey(character.class?.slug || character.class?.name || '')
    },
    rosterSpecKey(character) {
      return normalizeKey(character.spec?.slug || character.spec?.name || '')
    },
    collectRosterLoadouts(rankings, rosterLoadouts) {
      // Leaderboard rosters carry the exact talent string used in the run, which is both more
      // accurate than the profile's current loadout and free in terms of profile requests.
      rankings.forEach((entry) => {
        ;(entry.run?.roster || []).forEach((member) => {
          const character = member.character || {}
          const loadout = String(member.loadout || '').trim()
          const classKey = this.rosterClassKey(character)
          const specKey = this.rosterSpecKey(character)
          if (!loadout || !character.name || !classKey || !specKey) return
          const key = `${this.characterIdentityKey(character)}|${specKey}`
          if (rosterLoadouts.has(key)) return
          rosterLoadouts.set(key, {
            key,
            classKey,
            className: character.class?.name || classKey,
            specKey,
            specName: character.spec?.name || this.specLabel(specKey),
            loadout,
          })
        })
      })
    },
    characterIdentityKey(character) {
      return String(character.path || `${character.region?.slug}-${character.realm?.slug}-${character.name}`).toLowerCase()
    },
    leaderboardSources(staticData, season) {
      const regions = ['world', 'eu', 'us', 'kr', 'tw']
      const dungeons = (season?.dungeons?.length ? season.dungeons : staticData.dungeons || [])
        .map((dungeon) => dungeon.slug)
        .filter(Boolean)
      const sources = []
      const addSource = (region, dungeon, label) => {
        const key = `${region}:${dungeon}`
        if (sources.some((source) => source.key === key)) return
        sources.push({ key, region, dungeon, label })
      }

      addSource(this.filters.region, this.filters.dungeon, 'selected leaderboard')
      regions.forEach((region) => addSource(region, 'all', `${region.toUpperCase()} all dungeons`))
      dungeons.forEach((dungeon) => addSource('world', dungeon, `World ${dungeon}`))
      regions
        .filter((region) => region !== 'world')
        .forEach((region) => {
          dungeons.forEach((dungeon) => addSource(region, dungeon, `${region.toUpperCase()} ${dungeon}`))
        })

      return sources
    },
    async loadProfilesForSpecMinimums(runFilters, sources, publishPartialAnalysis) {
      const rankings = []
      const profiles = []
      const rosterLoadouts = new Map()
      const fetchedCharacterKeys = new Set()
      const failedCharacterAttempts = new Map()
      let characters = []
      let completedProfileRequests = 0
      let batchNumber = 1
      let batchRequestCount = 0
      let failedProfileRequests = 0
      let failedLeaderboardRequests = 0
      let specMismatchProfiles = 0

      const snapshot = () => ({
        characters,
        profiles,
        rosterLoadouts: Array.from(rosterLoadouts.values()),
        failedProfileRequests,
        failedLeaderboardRequests,
        specMismatchProfiles,
      })

      const buildResult = (overrides = {}) => ({
        ...snapshot(),
        requestLimitReached: false,
        batchLimitReached: false,
        ...overrides,
      })

      const prepareNextRequest = async () => {
        if (batchRequestCount < ANALYSIS_BATCH_REQUEST_COUNT) return true

        publishPartialAnalysis?.(snapshot())
        if (batchNumber >= MAX_ANALYSIS_BATCH_COUNT) {
          setAnalysisJobState({
            loadingMessage: `Stopped after ${MAX_ANALYSIS_BATCH_COUNT} Raider.io batches`,
          })
          return false
        }

        await waitForAnalysisCooldown()
        batchNumber += 1
        batchRequestCount = 0
        setAnalysisJobState({
          batchNumber,
          batchRequestCount,
          batchRequestLimit: ANALYSIS_BATCH_REQUEST_COUNT,
        })
        return true
      }

      const noteRequests = (count) => {
        batchRequestCount += count
        setAnalysisJobState({
          batchNumber,
          batchRequestCount,
          batchRequestLimit: ANALYSIS_BATCH_REQUEST_COUNT,
        })
      }

      for (const source of sources) {
        let idlePages = 0
        for (let page = 0; page < MAX_LEADERBOARD_PAGE_COUNT; page += LEADERBOARD_PAGE_BATCH_SIZE) {
          const pages = Array.from(
            { length: Math.min(LEADERBOARD_PAGE_BATCH_SIZE, MAX_LEADERBOARD_PAGE_COUNT - page) },
            (_, index) => page + index
          )

          setAnalysisJobState({
            loadingMessage: `Loading ${source.label} pages ${pages[0] + 1}-${pages[pages.length - 1] + 1}`,
          })

          const sourceFilters = {
            ...runFilters,
            region: source.region,
            dungeon: source.dungeon,
          }
          const runPages = []
          for (const pageNumber of pages) {
            if (!await prepareNextRequest()) {
              return buildResult({ batchLimitReached: true })
            }
            noteRequests(1)
            try {
              runPages.push(await getMythicPlusRuns({ ...sourceFilters, page: pageNumber }, { cache: false }))
            } catch (error) {
              if (error.status === 429) {
                failedLeaderboardRequests += 1
                setAnalysisJobState({ failedRequestCount: failedProfileRequests + failedLeaderboardRequests })
                setAnalysisJobState({
                  loadingMessage: `Raider.io request limit reached`,
                })
                return buildResult({ requestLimitReached: true })
              }
              failedLeaderboardRequests += 1
              setAnalysisJobState({ failedRequestCount: failedProfileRequests + failedLeaderboardRequests })
              runPages.push({ rankings: [] })
            }
          }
          const pageRankings = runPages.flatMap((pageData) => pageData.rankings || [])
          rankings.push(...pageRankings)
          this.collectRosterLoadouts(pageRankings, rosterLoadouts)
          characters = this.uniqueCharacters(rankings)

          const newCharacters = this.charactersNeededForSpecMinimums(characters, profiles, fetchedCharacterKeys)
          idlePages = newCharacters.length ? 0 : idlePages + 1

          setAnalysisJobState({
            loadingMessage: 'Fetching character gear',
            progressCurrent: completedProfileRequests,
            progressTotal: completedProfileRequests + newCharacters.length,
          })

          for (let index = 0; index < newCharacters.length;) {
            if (!await prepareNextRequest()) {
              return buildResult({ batchLimitReached: true })
            }
            const remainingBatchRequests = ANALYSIS_BATCH_REQUEST_COUNT - batchRequestCount
            const batchSize = Math.min(PROFILE_BATCH_SIZE, remainingBatchRequests)
            const batch = newCharacters.slice(index, index + batchSize)
            index += batch.length
            batch.forEach((character) => fetchedCharacterKeys.add(this.characterIdentityKey(character)))
            noteRequests(batch.length)
            const results = await Promise.allSettled(batch.map((character) => this.fetchProfile(character)))
            completedProfileRequests += results.length
            failedProfileRequests += results.filter((result) => result.status === 'rejected').length
            results.forEach((result, resultIndex) => {
              if (result.status !== 'rejected' || result.reason?.status === 429) return
              const key = this.characterIdentityKey(batch[resultIndex])
              const attempts = Number(failedCharacterAttempts.get(key) || 0) + 1
              failedCharacterAttempts.set(key, attempts)
              if (attempts < PROFILE_REQUEST_RETRY_LIMIT) fetchedCharacterKeys.delete(key)
            })
            const fetchedProfiles = results.filter((result) => result.status === 'fulfilled').map((result) => result.value)
            // Gear comes from the profile's currently active specialization. Only keep profiles
            // whose active spec is one the character actually played on the leaderboard.
            const matchingProfiles = fetchedProfiles.filter((profile) => this.profileMatchesRosterSpec(profile))
            specMismatchProfiles += fetchedProfiles.length - matchingProfiles.length
            profiles.push(...matchingProfiles)
            setAnalysisJobState({
              progressCurrent: completedProfileRequests,
              failedRequestCount: failedProfileRequests + failedLeaderboardRequests,
              specMismatchCount: specMismatchProfiles,
              sampleCompletion: this.sampleCompletionFromCounts(this.profileSpecCounts(profiles)),
            })

            if (results.some((result) => result.status === 'rejected' && result.reason?.status === 429)) {
              setAnalysisJobState({
                loadingMessage: `Raider.io request limit reached`,
              })
              return buildResult({ requestLimitReached: true })
            }
          }

          const specCounts = this.profileSpecCounts(profiles)
          const missingSpecs = this.missingSpecNames(specCounts)
          setAnalysisJobState({
            loadingMessage: missingSpecs.length
              ? `Scanning specs: ${missingSpecs.slice(0, 6).join(', ')}${missingSpecs.length > 6 ? ` +${missingSpecs.length - 6}` : ''}`
              : 'Scanning classes and specs complete',
            sampleCompletion: this.sampleCompletionFromCounts(specCounts),
          })

          if (page + pages.length >= MIN_LEADERBOARD_PAGE_COUNT && this.hasCompleteSample(profiles)) {
            return buildResult()
          }

          if (!pageRankings.length || idlePages >= SOURCE_IDLE_PAGE_LIMIT) {
            break
          }
        }
      }

      return buildResult()
    },
    charactersNeededForSpecMinimums(characters, profiles, fetchedCharacterKeys) {
      const specCounts = this.profileSpecCounts(profiles)
      return characters.filter((character) => {
        const key = this.characterIdentityKey(character)
        if (!key || fetchedCharacterKeys.has(key)) return false

        const classKey = this.characterClassKey(character)
        const specKeys = this.characterRosterSpecKeys(character)
        if (!classKey || !specKeys.length) return true

        const neededSpecKey = specKeys.find((specKey) => {
          return CLASS_SPECIALIZATIONS[classKey]?.includes(specKey)
            && (specCounts[classKey]?.[specKey] || 0) < MIN_CHARACTERS_PER_SPEC
        })
        if (!neededSpecKey) return false

        // Reserve the slot so one page does not queue far more characters than the spec needs.
        if (!specCounts[classKey]) specCounts[classKey] = {}
        specCounts[classKey][neededSpecKey] = (specCounts[classKey][neededSpecKey] || 0) + 1
        return true
      })
    },
    characterRosterSpecKeys(character) {
      if (character.rosterSpecKeys?.length) return character.rosterSpecKeys
      const specKey = this.characterSpecKey(character)
      return specKey ? [specKey] : []
    },
    profileMatchesRosterSpec(profile) {
      const rosterSpecKeys = profile.rosterSpecKeys || []
      if (!rosterSpecKeys.length) return true
      const activeSpecKey = normalizeKey(profile.active_spec_name || profile.spec || '')
      return Boolean(activeSpecKey) && rosterSpecKeys.includes(activeSpecKey)
    },
    characterClassKey(character) {
      const className = character.fallbackClassName
        || character.class?.name
        || character.class?.slug
        || character.class
      return className ? normalizeClassKey(className) : ''
    },
    characterSpecKey(character) {
      const specName = character.fallbackSpecName
        || character.spec?.name
        || character.spec?.slug
        || character.spec
      return specName ? normalizeKey(specName) : ''
    },
    missingSpecNames(specCounts) {
      return ALL_CLASSES.flatMap((classItem) => {
        return (CLASS_SPECIALIZATIONS[classItem.key] || []).filter((specKey) => {
          return (specCounts[classItem.key]?.[specKey] || 0) < MIN_CHARACTERS_PER_SPEC
        }).map((specKey) => `${classItem.name} ${this.specLabel(specKey)} ${specCounts[classItem.key]?.[specKey] || 0}/${MIN_CHARACTERS_PER_SPEC}`)
      })
    },
    sampleCompletionFromCounts(specCounts) {
      const specDeficits = ALL_CLASSES.flatMap((classItem) => {
        return (CLASS_SPECIALIZATIONS[classItem.key] || []).map((specKey) => {
          const count = specCounts[classItem.key]?.[specKey] || 0
          return {
            classKey: classItem.key,
            specKey,
            count,
            missing: Math.max(0, MIN_CHARACTERS_PER_SPEC - count),
          }
        })
      })

      // A class counts as complete once every one of its specializations reached the minimum.
      const classDeficits = ALL_CLASSES.map((classItem) => ({
        key: classItem.key,
        name: classItem.name,
        missing: specDeficits
          .filter((item) => item.classKey === classItem.key)
          .reduce((total, item) => total + item.missing, 0),
      }))

      return {
        classesDone: classDeficits.filter((item) => !item.missing).length,
        classesTotal: classDeficits.length,
        specsDone: specDeficits.filter((item) => !item.missing).length,
        specsTotal: specDeficits.length,
        missingClasses: classDeficits.filter((item) => item.missing).length,
        missingSpecs: specDeficits.filter((item) => item.missing).length,
        minimumProfilesNeeded: specDeficits.reduce((total, item) => total + item.missing, 0),
      }
    },
    specLabel(specKey) {
      return String(specKey || '')
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    },
    hasCompleteSample(profiles) {
      return this.hasMinimumProfilesPerSpec(profiles)
    },
    hasMinimumProfilesPerSpec(profiles) {
      const specCounts = this.profileSpecCounts(profiles)
      return ALL_CLASSES.every((classItem) => {
        return (CLASS_SPECIALIZATIONS[classItem.key] || []).every((specKey) => {
          return (specCounts[classItem.key]?.[specKey] || 0) >= MIN_CHARACTERS_PER_SPEC
        })
      })
    },
    profileSpecCounts(profiles) {
      return profiles.reduce((counts, profile) => {
        const className = profile.class || profile.class_name || profile.fallbackClassName || 'Unknown'
        const specName = profile.active_spec_name || profile.spec || profile.fallbackSpecName || 'Unknown'
        if (className === 'Unknown' || specName === 'Unknown') return counts
        const classKey = normalizeClassKey(className)
        const specKey = normalizeKey(specName)
        if (!CLASS_SPECIALIZATIONS[classKey]?.includes(specKey)) return counts
        if (!counts[classKey]) counts[classKey] = {}
        counts[classKey][specKey] = (counts[classKey][specKey] || 0) + 1
        return counts
      }, {})
    },
    async fetchProfile(character) {
      const profile = await getCharacterProfile({
        region: character.region?.slug,
        realm: character.realm?.slug,
        name: character.name,
        fields: 'gear,mythic_plus_scores_by_season:current',
      }, { cache: false })
      return {
        ...profile,
        fallbackClassName: character.fallbackClassName,
        fallbackSpecName: character.fallbackSpecName,
        rosterSpecKeys: character.rosterSpecKeys || [],
        fallbackRegionSlug: character.region?.slug,
        fallbackRealmSlug: character.realm?.slug,
      }
    },
    ensureSpecEntry(classes, classKey, className, specKey, specName) {
      if (!classes[classKey]) {
        classes[classKey] = {
          name: className,
          characterCount: 0,
          specs: {},
        }
      }

      if (!classes[classKey].specs[specKey]) {
        classes[classKey].specs[specKey] = {
          className,
          name: specName,
          specName,
          characterCount: 0,
          characters: [],
          characterKeys: new Set(),
          talentBuildCounts: {},
          talentSampleCount: 0,
          statTotals: {},
          statCounts: {},
          statSignalCounts: {},
          statSampleCount: 0,
          statSignalSampleCount: 0,
          slotCounts: {},
        }
      }

      return classes[classKey].specs[specKey]
    },
    aggregateProfiles(profiles, rosterLoadouts = []) {
      const classes = ALL_CLASSES.reduce((map, classItem) => {
        map[classItem.key] = {
          name: classItem.name,
          characterCount: 0,
          specs: {},
        }
        return map
      }, {})

      // Talent builds come from the leaderboard rosters: one loadout per character and spec.
      rosterLoadouts.forEach((entry) => {
        const spec = this.ensureSpecEntry(classes, entry.classKey, entry.className, entry.specKey, entry.specName)
        spec.talentSampleCount += 1
        if (!spec.talentBuildCounts[entry.loadout]) {
          spec.talentBuildCounts[entry.loadout] = {
            loadout: entry.loadout,
            label: `Build ${Object.keys(spec.talentBuildCounts).length + 1}`,
            count: 0,
          }
        }
        spec.talentBuildCounts[entry.loadout].count += 1
      })

      profiles.forEach((profile) => {
        const className = profile.class || profile.class_name || profile.fallbackClassName || 'Unknown'
        const specName = profile.active_spec_name || profile.spec || profile.fallbackSpecName || 'Unknown'
        if (className === 'Unknown' || specName === 'Unknown') return
        const classKey = normalizeClassKey(className)
        const specKey = normalizeKey(specName)

        this.ensureSpecEntry(classes, classKey, className, specKey, specName)

        classes[classKey].characterCount += 1
        classes[classKey].specs[specKey].characterCount += 1
        const sampleCharacter = this.sampleCharacterFromProfile(profile, className, specName)
        if (sampleCharacter && !classes[classKey].specs[specKey].characterKeys.has(sampleCharacter.key)) {
          classes[classKey].specs[specKey].characterKeys.add(sampleCharacter.key)
          classes[classKey].specs[specKey].characters.push(sampleCharacter)
        }

        const stats = this.profileStatValues(profile)
        if (Object.keys(stats).length) {
          classes[classKey].specs[specKey].statSampleCount += 1
          Object.entries(stats).forEach(([key, value]) => {
            classes[classKey].specs[specKey].statTotals[key] = (classes[classKey].specs[specKey].statTotals[key] || 0) + value
            classes[classKey].specs[specKey].statCounts[key] = (classes[classKey].specs[specKey].statCounts[key] || 0) + 1
          })
        }

        const statSignals = this.profileStatSignals(profile)
        if (Object.keys(statSignals).length) {
          classes[classKey].specs[specKey].statSignalSampleCount += 1
          Object.entries(statSignals).forEach(([key, signal]) => {
            if (!classes[classKey].specs[specKey].statSignalCounts[key]) {
              const stat = STAT_KEYS.find((item) => item.key === key)
              classes[classKey].specs[specKey].statSignalCounts[key] = {
                key,
                label: stat?.label || key,
                count: 0,
                profileCount: 0,
                gemCount: 0,
                enchantCount: 0,
                itemStatCount: 0,
              }
            }
            classes[classKey].specs[specKey].statSignalCounts[key].profileCount += 1
            classes[classKey].specs[specKey].statSignalCounts[key].count += signal.count
            classes[classKey].specs[specKey].statSignalCounts[key].gemCount += signal.gemCount
            classes[classKey].specs[specKey].statSignalCounts[key].enchantCount += signal.enchantCount
            classes[classKey].specs[specKey].statSignalCounts[key].itemStatCount += signal.itemStatCount
          })
        }

        this.normalizedGearItems(profile).forEach((item) => {
          const slotKey = this.aggregateSlotKey(item.slotKey)
          const itemId = item.item_id || item.id
          if (!slotKey || !itemId || !SLOT_LABELS[slotKey]) return

          const slotCounts = classes[classKey].specs[specKey].slotCounts
          if (!slotCounts[slotKey]) slotCounts[slotKey] = {}
          if (!slotCounts[slotKey][itemId]) {
            slotCounts[slotKey][itemId] = {
              itemId,
              name: item.name || `Item ${itemId}`,
              icon: item.icon || '',
              itemLevel: item.item_level || item.itemLevel || null,
              bonuses: item.bonuses || [],
              gems: item.gems || [],
              enchant: item.enchant || null,
              enchant_id: item.enchant_id || null,
              count: 0,
            }
          }
          slotCounts[slotKey][itemId].count += 1
        })
      })

      Object.values(classes).forEach((classItem) => {
        Object.values(classItem.specs).forEach((spec) => {
          spec.slots = Object.entries(spec.slotCounts).reduce((slots, [slot, items]) => {
            slots[slot] = Object.values(items)
              .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
              .slice(0, TOP_ITEM_LIMIT)
              .map((item) => ({
                ...item,
                share: Math.round((item.count / Math.max(spec.characterCount, 1)) * 100),
              }))
            return slots
          }, {})
          spec.talentBuilds = Object.values(spec.talentBuildCounts)
            .sort((left, right) => right.count - left.count || left.loadout.localeCompare(right.loadout))
            .map((build, index) => ({
              ...build,
              rank: index + 1,
              label: `Build ${index + 1}`,
              share: Math.round((build.count / Math.max(spec.talentSampleCount, 1)) * 100),
              shortLoadout: this.shortLoadoutText(build.loadout),
            }))
          spec.stats = STAT_KEYS.map((stat) => {
            const count = spec.statCounts[stat.key] || 0
            if (!count) return null
            return {
              key: stat.key,
              label: stat.label,
              count,
              average: Math.round(spec.statTotals[stat.key] / count).toLocaleString(),
              rawAverage: spec.statTotals[stat.key] / count,
            }
          })
            .filter(Boolean)
            .sort((left, right) => right.rawAverage - left.rawAverage)
          spec.statSignals = Object.values(spec.statSignalCounts)
            .sort((left, right) => right.profileCount - left.profileCount || right.count - left.count || left.label.localeCompare(right.label))
            .map((signal) => ({
              ...signal,
              sampleCount: spec.statSignalSampleCount,
              share: Math.round((signal.profileCount / Math.max(spec.statSignalSampleCount, 1)) * 100),
              sources: [
                signal.gemCount ? `${signal.gemCount} gems` : '',
                signal.enchantCount ? `${signal.enchantCount} enchants` : '',
                signal.itemStatCount ? `${signal.itemStatCount} item stats` : '',
              ].filter(Boolean).join(' / '),
            }))
          spec.characters.sort((left, right) => right.score - left.score || left.name.localeCompare(right.name))
          delete spec.slotCounts
          delete spec.characterKeys
          delete spec.talentBuildCounts
          delete spec.statTotals
          delete spec.statCounts
          delete spec.statSignalCounts
        })
      })

      return classes
    },
    shortLoadoutText(loadout) {
      const value = String(loadout || '')
      return value.length > 44 ? `${value.slice(0, 44)}...` : value
    },
    talentCalculatorUrl(loadout) {
      return loadout ? `https://www.wowhead.com/talent-calc/blizzard/${encodeURIComponent(loadout)}` : 'https://www.wowhead.com/talent-calc'
    },
    talentEmbedUrl(loadout) {
      return loadout ? `https://www.wowhead.com/talent-calc/embed/blizzard/${encodeURIComponent(loadout)}` : ''
    },
    profileStatValues(profile) {
      const candidates = [
        profile.stats,
        profile.character_stats,
        profile.characterStats,
        profile.gear?.stats,
        profile.gear?.character_stats,
      ].filter(Boolean)
      const values = {}

      candidates.forEach((stats) => {
        STAT_KEYS.forEach((stat) => {
          if (values[stat.key]) return
          const rawValue = stat.aliases.map((alias) => stats[alias]).find((value) => value !== undefined && value !== null)
          const numericValue = Number(rawValue?.value ?? rawValue?.rating ?? rawValue)
          if (Number.isFinite(numericValue) && numericValue > 0) {
            values[stat.key] = numericValue
          }
        })
      })

      return values
    },
    profileStatSignals(profile) {
      const signals = {}
      const addSignal = (statKey, sourceKey) => {
        if (!statKey) return
        if (!signals[statKey]) {
          signals[statKey] = {
            count: 0,
            gemCount: 0,
            enchantCount: 0,
            itemStatCount: 0,
          }
        }
        signals[statKey].count += 1
        signals[statKey][sourceKey] += 1
      }

      this.normalizedGearItems(profile).forEach((item) => {
        const gems = [
          ...(Array.isArray(item.gems_detail) ? item.gems_detail : Object.values(item.gems_detail || {})),
          ...(Array.isArray(item.gems) ? item.gems : Object.values(item.gems || {})),
        ]
        gems.forEach((gem) => {
          this.statKeysFromText(this.statSearchText(gem)).forEach((statKey) => addSignal(statKey, 'gemCount'))
        })

        const enchantText = this.statSearchText([
          item.enchant,
          item.enchants_detail,
          item.enchant_name,
          item.enchantName,
          item.enchant_display_string,
          item.enchantDisplayString,
        ])
        this.statKeysFromText(enchantText).forEach((statKey) => addSignal(statKey, 'enchantCount'))

        this.itemStatTexts(item).forEach((text) => {
          this.statKeysFromText(text).forEach((statKey) => addSignal(statKey, 'itemStatCount'))
        })
      })

      return signals
    },
    itemStatTexts(item) {
      const stats = item.stats || item.item_stats || item.itemStats || item.bonus_stats || item.bonusStats
      if (!stats) return []
      if (Array.isArray(stats)) {
        return stats.map((stat) => this.statSearchText(stat)).filter(Boolean)
      }
      if (typeof stats === 'object') {
        return Object.entries(stats).map(([key, value]) => `${key} ${this.statSearchText(value)}`).filter(Boolean)
      }
      return [this.statSearchText(stats)].filter(Boolean)
    },
    statSearchText(value) {
      if (!value) return ''
      if (Array.isArray(value)) {
        return value.map((item) => this.statSearchText(item)).filter(Boolean).join(' ')
      }
      if (typeof value === 'object') {
        return [
          value.name,
          value.display_string,
          value.displayString,
          value.type,
          value.stat,
          value.stat_type,
          value.statType,
          value.tooltip,
        ].map((item) => this.statSearchText(item)).filter(Boolean).join(' ')
      }
      return String(value)
    },
    statKeyFromText(text) {
      return this.statKeysFromText(text)[0] || ''
    },
    statKeysFromText(text) {
      const value = String(text || '').toLowerCase()
      if (!value) return []
      return STAT_KEYS
        .filter((stat) => stat.aliases.some((alias) => new RegExp(`\\b${String(alias).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(value)))
        .map((stat) => stat.key)
    },
    sampleCharacterFromProfile(profile, className, specName) {
      const region = String(profile.region || profile.fallbackRegionSlug || '').toLowerCase()
      const realm = profile.realm || profile.realm_slug || profile.fallbackRealmSlug || ''
      const name = profile.name || ''
      if (!region || !realm || !name) return null

      const score = Math.round(profile.mythic_plus_scores_by_season?.[0]?.scores?.all || 0)
      const itemLevel = Math.round(profile.gear?.item_level_equipped || profile.gear?.item_level || 0)
      const realmSlug = String(profile.realm_slug || profile.fallbackRealmSlug || realm).toLowerCase().replace(/\s+/g, '-')
      const faction = profile.faction || ''
      const race = profile.race || ''

      return {
        key: `${region}:${realmSlug}:${String(name).toLowerCase()}`,
        name,
        realm,
        realmSlug,
        region,
        regionLabel: region.toUpperCase(),
        className,
        specName,
        thumbnailUrl: profile.thumbnail_url || '',
        faction,
        factionLabel: faction || '-',
        race,
        raceLabel: race || '-',
        score,
        scoreLabel: score ? String(score) : '-',
        itemLevel,
        itemLevelLabel: itemLevel ? String(itemLevel) : '-',
        profileUrl: profile.profile_url || `https://raider.io/characters/${region}/${realmSlug}/${encodeURIComponent(name)}`,
      }
    },
    normalizedGearItems(profile) {
      const items = profile.gear?.items
      if (!items) return []

      if (Array.isArray(items)) {
        return items.map((item, index) => this.normalizeGearItem(item, item.slot || item.slot_key || `${index}`))
      }

      return Object.entries(items).map(([slotKey, item]) => this.normalizeGearItem(item, slotKey))
    },
    normalizeGearItem(item, slotKey) {
      return {
        ...(item || {}),
        slotKey: normalizeSlotKey(slotKey || item?.slot || item?.inventory_type?.type),
      }
    },
    bestItem(slot) {
      return this.selectedBestItems[slot] || null
    },
    itemForDisplaySlot(slot) {
      const items = this.itemsForSlot(slot)
      if (slot === 'finger2' || slot === 'trinket2') {
        return items[1] || null
      }
      return items[0] || null
    },
    itemsForSlot(slot) {
      return this.selectedSpecData?.slots?.[this.aggregateSlotKey(slot)] || []
    },
    aggregateSlotKey(slot) {
      const normalizedSlot = normalizeSlotKey(slot)
      if (normalizedSlot === 'finger1' || normalizedSlot === 'finger2') return 'finger'
      if (normalizedSlot === 'trinket1' || normalizedSlot === 'trinket2') return 'trinket'
      return normalizedSlot
    },
    itemName(slot) {
      return this.bestItem(slot)?.name || this.slotLabel(slot)
    },
    itemMeta(slot) {
      const item = this.bestItem(slot)
      if (!item && (slot === 'shirt' || slot === 'tabard')) return '-'
      return item ? `${item.count} equipped · ${item.share}%` : 'No data'
    },
    slotLabel(slot) {
      return SLOT_LABELS[slot] || 'Empty slot'
    },
    wowheadItemUrl(item) {
      const itemId = item?.itemId || item?.item_id || item?.id
      return itemId ? `https://www.wowhead.com/item=${itemId}` : 'https://www.wowhead.com/'
    },
    wowheadItemData(item) {
      const itemId = item?.itemId || item?.item_id || item?.id
      if (!itemId) return null
      const params = [`item=${itemId}`]
      const itemLevel = item.itemLevel || item.item_level
      if (itemLevel) params.push(`ilvl=${itemLevel}`)
      if (Array.isArray(item.bonuses) && item.bonuses.length) params.push(`bonus=${item.bonuses.join(':')}`)
      if (Array.isArray(item.gems) && item.gems.length) params.push(`gems=${item.gems.map((gem) => gem.item_id || gem.id || gem).join(':')}`)
      // Raider.io returns `enchant` as a numeric enchantment id, not an object.
      const enchantId = typeof item.enchant === 'object' ? item.enchant?.id : item.enchant
      if (enchantId || item.enchant_id) params.push(`ench=${enchantId || item.enchant_id}`)
      return params.join('&')
    },
    openSlot(slot) {
      if (!this.itemsForSlot(slot).length) return
      this.activeSlot = slot
      this.closingSlot = false
      document.body.classList.add('is-scroll-locked')
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    closeSlot() {
      if (!this.activeSlot || this.closingSlot) return
      this.closingSlot = true
      window.setTimeout(() => {
        this.activeSlot = null
        this.closingSlot = false
        document.body.classList.remove('is-scroll-locked')
      }, 190)
    },
    closeSlotOnEscape(event) {
      if (event.key !== 'Escape') return
      if (this.activeTalentBuild) {
        this.closeTalentBuild()
        return
      }
      if (this.activeSlot) {
        this.closeSlot()
      }
    },
    openTalentBuild(build) {
      if (!build?.loadout) return
      this.activeTalentBuild = build
      this.closingTalentBuild = false
      document.body.classList.add('is-scroll-locked')
    },
    async copyTalentLoadout(build) {
      const loadout = build?.loadout || ''
      if (!loadout) return

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(loadout)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = loadout
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$store.dispatch('notify', {
          type: 'success',
          title: 'Talent code copied',
          description: `${build.label} loadout is ready to paste.`,
        })
      } catch (error) {
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Copy failed',
          description: 'Unable to copy this talent loadout.',
        })
      }
    },
    closeTalentBuild() {
      if (!this.activeTalentBuild || this.closingTalentBuild) return
      this.closingTalentBuild = true
      window.setTimeout(() => {
        this.activeTalentBuild = null
        this.closingTalentBuild = false
        document.body.classList.remove('is-scroll-locked')
      }, 190)
    },
    resetCharacterList() {
      this.characterSearch = ''
      this.characterPage = 1
    },
    resetTalentList() {
      this.talentPage = 1
    },
    factionKey(faction) {
      const key = String(faction || '').toLowerCase()
      return key === 'alliance' || key === 'horde' ? key : 'mixed'
    },
    factionIcon(faction) {
      return FACTION_ICONS[this.factionKey(faction)]
    },
    async addSampleCharacterToFavorites(character) {
      if (!character?.region || !character?.realmSlug || !character?.name || this.addingFavoriteKey) return

      this.addingFavoriteKey = character.key
      const loadingId = await this.$store.dispatch('startGlobalLoading', {
        id: `gear-insights:favorite:${character.key}`,
        message: `Loading ${character.name} profile...`,
      })

      try {
        const profile = await getCharacterProfile({
          region: character.region,
          realm: character.realmSlug,
          name: character.name,
          fields: [
            'gear',
            'talents',
            'guild',
            'raid_progression:current-expansion',
            'mythic_plus_scores_by_season:current',
            'mythic_plus_best_runs:all',
            'mythic_plus_recent_runs',
            'mythic_plus_ranks',
            'realm',
          ].join(','),
        }, { cache: false })
        this.$store.dispatch('addFavorite', { character: profile, source: 'gear_insights' })
      } catch (error) {
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Favorite failed',
          description: `Unable to add ${character.name} to favorites.`,
        })
      } finally {
        this.addingFavoriteKey = ''
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    ensureWowheadTooltips() {
      ensureWowheadTooltips()
    },
    refreshWowheadTooltips() {
      this.$nextTick(() => {
        refreshWowheadTooltips()
      })
    },
  },
}
</script>
